import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const PortfolioBuilder = ({ portfolio, onAddProject, onEditProject }) => {
  const [activeSection, setActiveSection] = useState('projects');

  const sections = [
    { id: 'projects', label: 'Projects', icon: 'FolderOpen' },
    { id: 'skills', label: 'Skills', icon: 'Code' },
    { id: 'achievements', label: 'Achievements', icon: 'Award' },
    { id: 'feedback', label: 'AI Feedback', icon: 'Bot' }
  ];

  const getSkillLevel = (level) => {
    switch (level) {
      case 'Expert': return { color: 'text-success', bg: 'bg-success', width: '90%' };
      case 'Advanced': return { color: 'text-accent', bg: 'bg-accent', width: '75%' };
      case 'Intermediate': return { color: 'text-warning', bg: 'bg-warning', width: '60%' };
      case 'Beginner': return { color: 'text-muted-foreground', bg: 'bg-muted-foreground', width: '40%' };
      default: return { color: 'text-muted-foreground', bg: 'bg-muted-foreground', width: '20%' };
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Portfolio Builder</h2>
          <p className="text-muted-foreground">Showcase your skills and projects to potential employers</p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            iconName="Eye"
            iconPosition="left"
          >
            Preview
          </Button>
          <Button
            variant="default"
            iconName="Share"
            iconPosition="left"
            className="morphing-cta"
          >
            Share Portfolio
          </Button>
        </div>
      </div>

      {/* Portfolio Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        {[
          { label: 'Profile Views', value: '1,234', icon: 'Eye', color: 'text-accent' },
          { label: 'Projects', value: portfolio.projects.length, icon: 'FolderOpen', color: 'text-success' },
          { label: 'Skills', value: portfolio.skills.length, icon: 'Code', color: 'text-warning' },
          { label: 'Achievements', value: portfolio.achievements.length, icon: 'Award', color: 'text-cyber-purple' }
        ].map((stat, index) => (
          <div key={index} className="holographic-card p-4">
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 rounded-lg bg-muted/30 flex items-center justify-center`}>
                <Icon name={stat.icon} size={20} className={stat.color} />
              </div>
              <div>
                <div className="text-xl font-bold text-foreground">{stat.value}</div>
                <div className="text-xs text-muted-foreground">{stat.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Section Navigation */}
      <div className="flex space-x-1 bg-muted/20 p-1 rounded-lg">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 ${
              activeSection === section.id
                ? 'bg-accent text-white shadow-glow'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={section.icon} size={16} />
            <span>{section.label}</span>
          </button>
        ))}
      </div>

      {/* Content Sections */}
      <div className="min-h-[400px]">
        {/* Projects Section */}
        {activeSection === 'projects' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Your Projects</h3>
              <Button
                variant="default"
                size="sm"
                iconName="Plus"
                iconPosition="left"
                onClick={onAddProject}
                className="morphing-cta"
              >
                Add Project
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {portfolio.projects.map((project) => (
                <div key={project.id} className="holographic-card group">
                  <div className="relative overflow-hidden rounded-t-lg h-40">
                    <Image
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent"></div>
                    
                    <div className="absolute top-3 right-3">
                      <Button
                        variant="ghost"
                        size="icon"
                        iconName="Edit"
                        onClick={() => onEditProject(project.id)}
                        className="bg-background/80 backdrop-blur-sm hover:bg-background/90"
                      />
                    </div>
                  </div>

                  <div className="p-4">
                    <h4 className="font-semibold text-foreground mb-2">{project.title}</h4>
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{project.description}</p>
                    
                    <div className="flex flex-wrap gap-2 mb-3">
                      {project.technologies.map((tech, index) => (
                        <span 
                          key={index}
                          className="px-2 py-1 bg-muted/30 text-xs text-muted-foreground rounded-md"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 text-sm">
                        <div className="flex items-center space-x-1 text-accent">
                          <Icon name="Star" size={14} />
                          <span>{project.stars}</span>
                        </div>
                        <div className="flex items-center space-x-1 text-muted-foreground">
                          <Icon name="Eye" size={14} />
                          <span>{project.views}</span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button variant="ghost" size="icon" iconName="ExternalLink" />
                        <Button variant="ghost" size="icon" iconName="Github" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Skills Section */}
        {activeSection === 'skills' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Technical Skills</h3>
              <Button
                variant="outline"
                size="sm"
                iconName="Plus"
                iconPosition="left"
              >
                Add Skill
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {portfolio.skills.map((skill, index) => {
                const levelInfo = getSkillLevel(skill.level);
                return (
                  <div key={index} className="holographic-card p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-lg bg-muted/30 flex items-center justify-center">
                          <Icon name={skill.icon} size={20} className={levelInfo.color} />
                        </div>
                        <div>
                          <h4 className="font-medium text-foreground">{skill.name}</h4>
                          <p className="text-sm text-muted-foreground">{skill.category}</p>
                        </div>
                      </div>
                      <span className={`text-sm font-medium ${levelInfo.color}`}>{skill.level}</span>
                    </div>

                    <div className="mb-3">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-muted-foreground">Proficiency</span>
                        <span className="text-foreground">{skill.percentage}%</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${levelInfo.bg}`}
                          style={{ width: levelInfo.width }}
                        ></div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Experience: {skill.experience}</span>
                      <div className="flex items-center space-x-1 text-warning">
                        <Icon name="Award" size={14} />
                        <span>{skill.projects} projects</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Achievements Section */}
        {activeSection === 'achievements' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">Achievements & Certifications</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {portfolio.achievements.map((achievement, index) => (
                <div key={index} className="holographic-card p-4">
                  <div className="flex items-start space-x-3">
                    <div className={`w-12 h-12 rounded-lg ${achievement.bgColor} flex items-center justify-center shadow-glow`}>
                      <Icon name={achievement.icon} size={24} color="white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-foreground mb-1">{achievement.title}</h4>
                      <p className="text-sm text-muted-foreground mb-2">{achievement.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">{achievement.date}</span>
                        <Button variant="ghost" size="icon" iconName="ExternalLink" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI Feedback Section */}
        {activeSection === 'feedback' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">AI Portfolio Analysis</h3>
              <Button
                variant="default"
                size="sm"
                iconName="RefreshCw"
                iconPosition="left"
                className="morphing-cta"
              >
                Analyze Portfolio
              </Button>
            </div>

            <div className="holographic-card p-6">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-accent to-cyber-purple flex items-center justify-center shadow-glow">
                  <Icon name="Bot" size={24} color="white" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-1">AI Portfolio Assistant</h4>
                  <p className="text-sm text-muted-foreground">Get personalized recommendations to improve your portfolio</p>
                </div>
              </div>

              <div className="space-y-4">
                {portfolio.feedback.map((item, index) => (
                  <div key={index} className={`p-4 rounded-lg border-l-4 ${
                    item.type === 'strength' ? 'bg-success/10 border-success' :
                    item.type === 'improvement'? 'bg-warning/10 border-warning' : 'bg-accent/10 border-accent'
                  }`}>
                    <div className="flex items-start space-x-3">
                      <Icon 
                        name={
                          item.type === 'strength' ? 'CheckCircle' :
                          item.type === 'improvement'? 'AlertTriangle' : 'Lightbulb'
                        } 
                        size={20} 
                        className={
                          item.type === 'strength' ? 'text-success' :
                          item.type === 'improvement'? 'text-warning' : 'text-accent'
                        }
                      />
                      <div>
                        <h5 className="font-medium text-foreground mb-1">{item.title}</h5>
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortfolioBuilder;